/**
 * Where an agent's sign-in page opens (the owner, 21/10/2026): the agent's site (claude.ai, chatgpt.com) remembers the
 * account already signed in in that browser, so connecting a second account — another person's, on this computer —
 * means a private window or another browser. "Conectar" opens it in the default browser; its arrow offers a private
 * window, or another browser chosen from those installed here (browsers.rs). Desktop only, like the sign-in itself.
 */

import { useEffect, useState } from 'react'
import { ChevronDown, ExternalLink } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { Dropdown, DropdownItem } from './Dropdown'
import type { InstalledBrowser } from '../lib/browsers'

export function SignInOpener({
  url,
  list,
  open,
}: {
  url: string
  list: () => Promise<InstalledBrowser[]>
  open: (url: string, browser: string | null, privately: boolean) => Promise<void>
}) {
  const { t } = useI18n()
  const [browsers, setBrowsers] = useState<InstalledBrowser[]>([])
  const [choosing, setChoosing] = useState(false)
  const [chosen, setChosen] = useState<string>('')
  const [privately, setPrivately] = useState(false)
  const [problem, setProblem] = useState(false)

  useEffect(() => {
    let alive = true
    void list().then((found) => {
      if (!alive) return
      setBrowsers(found)
      setChosen((current) => current || (found.find((b) => !b.default) ?? found[0])?.id || '')
    }).catch(() => {})
    return () => {
      alive = false
    }
  }, [list])

  const go = (browser: string | null, inPrivate: boolean) => {
    setProblem(false)
    void open(url, browser, inPrivate).catch(() => setProblem(true))
  }

  return (
    <div className="stack stack--tight sign-in-opener">
      <div className="row row--tight">
        <div className="split-button">
          <button type="button" className="btn btn--primary split-button__main" onClick={() => go(null, false)}>
            <ExternalLink size={14} aria-hidden /> {t('signInOpener.connect')}
          </button>
          <Dropdown
            label={t('signInOpener.more')}
            align="end"
            buttonClassName="btn btn--primary split-button__arrow"
            button={<ChevronDown size={14} aria-hidden />}
          >
            {(close) => (
              <>
                <DropdownItem label={t('signInOpener.connect')} onSelect={() => { close(); go(null, false) }} />
                <DropdownItem label={t('signInOpener.private')} onSelect={() => { close(); go(null, true) }} />
                <DropdownItem label={t('signInOpener.otherBrowser')} onSelect={() => { close(); setChoosing(true) }} />
              </>
            )}
          </Dropdown>
        </div>
      </div>
      {choosing && (
        <div className="row row--tight row--wrap">
          <select className="select" value={chosen} aria-label={t('signInOpener.browser')} onChange={(e) => setChosen(e.target.value)}>
            {browsers.map((b) => (
              <option key={b.id} value={b.id}>{b.default ? t('signInOpener.isDefault', { name: b.name }) : b.name}</option>
            ))}
          </select>
          <label className="row row--tight small">
            <input type="checkbox" checked={privately} onChange={(e) => setPrivately(e.target.checked)} />
            {t('signInOpener.privately')}
          </label>
          <button type="button" className="btn" disabled={!chosen} onClick={() => go(chosen, privately)}>
            {t('signInOpener.openChosen')}
          </button>
        </div>
      )}
      <span className="tiny muted">{t('signInOpener.hint')}</span>
      {problem && <span className="tiny field__error" role="alert">{t('signInOpener.failed')}</span>}
    </div>
  )
}
